/** @flow */

import React from "react";
import { localize } from "redux-i18n";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  renderComboBox,
} from "@components";
import { Field, reduxForm, formValueSelector } from "redux-form";
import type { FormProps } from "redux-form";
import { compose } from "redux";
import { connect } from "react-redux";
import type { State } from "@store/reducers";
import type { SelectedTask } from "@modules/reporting/reducer/tasks/types";

type PropsFromState = $Exact<{
  lateSubmissionReasonId: number,
}>;

type OwnProps = $Exact<{
  isOpen: boolean,
  onDismissHandler: () => void,
  onSubmit: (data: Object) => void,
  dataToSubmit: $Exact<{
    clientId: number,
    taskId: number,
    task: SelectedTask,
  }>,
  selectedTask: ?SelectedTask,
}>;

type Props = $Exact<{
  ...PropsFromLocalize,
  ...PropsFromState,
  ...$Exact<FormProps>, // https://stackoverflow.com/a/44771268/220839
  ...OwnProps,
}>;

export class LateReasonModal extends React.Component<Props> {
  submitForm = () => {
    this.props.onSubmit({
      ...this.props.dataToSubmit,
      task: {
        ...this.props.dataToSubmit.task,
        lateSubmissionReason: this.props.selectedTask.lateSubmissionReasons.find(
          item => item.id === this.props.lateSubmissionReasonId,
        ),
      },
    });
  };
  get options() {
    return this.props.selectedTask.lateSubmissionReasons.map(item => ({
      label: item.reason,
      value: item.id,
    }));
  }

  get daysCount() {
    return this.props.selectedTask.kpiTargetInHours / 24;
  }

  get lateTimeCount() {
    const kpiTarget = this.props.selectedTask.kpiTargetInHours;

    // only days
    if (kpiTarget % 24 === 0) {
      return this.props.t("DAYS_COUNT", {
        daysCount: kpiTarget / 24,
      });
    }

    // less than a day, we show only hours
    if (kpiTarget < 24) {
      return this.props.t("HOURS_COUNT", {
        hoursCount: kpiTarget,
      });
    }

    // days and hours
    return this.props.t("DAYS_AND_HOURS_COUNT", {
      daysCount: Math.floor(kpiTarget / 24),
      hoursCount: kpiTarget % 24,
    });
  }

  render() {
    return (
      <Modal className="modal-small">
        <ModalHeader toggle={this.props.onDismissHandler}>
          {this.props.t("LATE_SUBMISSION")}
        </ModalHeader>
        <ModalBody className="d-flex justify-content-center align-items-center flex-column">
          <p className="normal-font-size">
            {this.props.t("LATE_REASON_DESCRIPTION", { lateTimeCount: this.lateTimeCount })}
          </p>
          <Form onSubmit={this.props.handleSubmit} className="d-flex w-100 justify-content-center">
            <FormGroup className="w-50">
              <Label className="small-font-size text-grey">{this.props.t("LATE_REASON")}</Label>
              <Field
                name="lateSubmissionReasonId"
                component={renderComboBox}
                options={this.options}
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.props.onDismissHandler}>
            {this.props.t("CANCEL")}
          </Button>
          <Button onClick={this.submitForm} disabled={!this.props.lateSubmissionReasonId}>
            {this.props.t("SET")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const selector = formValueSelector("late-reason");

const mapStateToProps = (state: State): PropsFromState => ({
  lateSubmissionReasonId: selector(state, "lateSubmissionReasonId"),
});

export default compose(
  localize(),
  reduxForm({
    form: "late-reason",
  }),
  connect(mapStateToProps, null),
)(LateReasonModal);
